import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { SessionUser } from '../../shared/contracts';
import type { MailboxStatus } from '../../domain/mailbox-status';
import { api } from '../api';
import { MailboxRecovery } from './MailboxRecovery';

function MailboxDetailsContent({ user }: { user: Pick<SessionUser, 'role'> }) {
  const [status, setStatus] = useState<MailboxStatus | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const load = async () => {
    try { setStatus((await api<{ status: MailboxStatus }>('/api/mailbox/status')).status); setError(''); }
    catch { setError('読み込めませんでした'); }
  };
  useEffect(() => { void load(); }, []);
  return <div className="mail-status-content">
    {error ? <p role="alert">{error} <button className="button secondary" onClick={() => void load()}>再読み込み</button></p> : !status ? <p role="status">読み込み中…</p> : <>
      <p>{({ normal: '自動チェック中', waiting: '初回チェック待ち', delayed: '自動チェックを待っています', reconnect_required: 'Googleの再接続が必要' })[status.state]}</p>
      <small>最終完了：{status.lastSuccessAt ? new Date(status.lastSuccessAt).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' }) : 'まだありません'}</small>
      {status.state === 'reconnect_required' && <p><a className="button primary" href="/api/auth/google/start">Googleを再接続</a></p>}
      <MailboxRecovery user={user} status={status} disabled={busy || status.state === 'reconnect_required'} onBusy={(value) => {
        if (value && busyRef.current) return false;
        busyRef.current = value; setBusy(value); return true;
      }} onDone={load} />
    </>}
  </div>;
}

export function MailboxDetails({ user }: { user: Pick<SessionUser, 'role'> }) {
  const { hash } = useLocation();
  const [opened, setOpened] = useState(hash === '#mail-status');
  const detailsRef = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (hash !== '#mail-status') return;
    setOpened(true);
    const frame = window.requestAnimationFrame(() => detailsRef.current?.scrollIntoView({ block: 'start' }));
    return () => window.cancelAnimationFrame(frame);
  }, [hash]);
  return <details ref={detailsRef} id="mail-status" className="panel quiet-details" open={opened} onToggle={(event) => setOpened(event.currentTarget.open)}>
    <summary>メールの確認状況</summary>
    {opened && <MailboxDetailsContent user={user} />}
  </details>;
}
